// screens/business/BusinessPaymentSystem.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList,
  SafeAreaView, Modal, ActivityIndicator, Animated, Dimensions,
  StatusBar, Alert, TextInput, Switch, RefreshControl, Share, Linking,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import StripePaymentIntegration from './BusinessStripePayment';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');
const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const C = {
  primary:  '#0e4e96',
  p2:       '#1e7be2',
  gold:     '#fdb913',
  success:  '#10B981',
  warning:  '#F59E0B',
  danger:   '#EF4444',
  purple:   '#7C3AED',
  teal:     '#19abb5',
  bg:       '#F0F4FA',
  card:     '#FFFFFF',
  border:   '#E2E8F0',
  text:     '#1E293B',
  sub:      '#64748B',
  light:    '#94A3B8',
};

const getHeaders = async () => {
  const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || '';
  return { 'X-API-Key': API_KEY, 'Authorization': `Bearer ${tok}`, 'Content-Type': 'application/json' };
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtCurrency = (amount, cur = 'GBP') => `£${Math.abs(parseFloat(amount || 0)).toFixed(2)}`;

const getStatusStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'active': case 'completed': case 'paid': return { bg:'#D1FAE5', text:'#065F46' };
    case 'pending':                                return { bg:'#FEF3C7', text:'#92400E' };
    case 'overdue':                                return { bg:'#FEE2E2', text:'#991B1B' };
    case 'cancelled': case 'failed':               return { bg:'#F3F4F6', text:'#374151' };
    case 'trial':                                  return { bg:'#EDE9FE', text:'#5B21B6' };
    default:                                       return { bg:'#EFF6FF', text:'#1E40AF' };
  }
};

const daysUntil = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / (1000*60*60*24));
};

const newInvoiceNum = () => `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`;

// ── Generate invoice HTML for print/PDF ───────────────────────────────────────
const buildInvoiceHTML = (inv, company) => `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; background:#fff; color:#1E293B; }
  .page { padding:40px; max-width:800px; margin:0 auto; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #0e4e96; padding-bottom:24px; margin-bottom:32px; }
  .logo-area h1 { font-size:28px; font-weight:900; color:#0e4e96; letter-spacing:-1px; }
  .logo-area p { color:#64748B; font-size:13px; margin-top:4px; }
  .invoice-meta { text-align:right; }
  .invoice-meta .inv-num { font-size:22px; font-weight:800; color:#0e4e96; }
  .invoice-meta .inv-date { color:#64748B; font-size:12px; margin-top:4px; }
  .badge { display:inline-block; background:#D1FAE5; color:#065F46; padding:4px 12px; border-radius:20px; font-size:11px; font-weight:700; margin-top:8px; }
  .badge.pending { background:#FEF3C7; color:#92400E; }
  .parties { display:flex; gap:40px; margin-bottom:32px; }
  .party h3 { font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#94A3B8; margin-bottom:8px; }
  .party p { font-size:14px; color:#1E293B; line-height:1.6; }
  .party strong { font-weight:700; font-size:15px; }
  table { width:100%; border-collapse:collapse; margin-bottom:24px; }
  thead th { background:#0e4e96; color:#fff; padding:12px 16px; text-align:left; font-size:12px; text-transform:uppercase; letter-spacing:0.5px; }
  tbody tr { border-bottom:1px solid #E2E8F0; }
  tbody td { padding:12px 16px; font-size:13px; }
  tbody tr:nth-child(even) { background:#F8FAFC; }
  .totals { display:flex; flex-direction:column; align-items:flex-end; gap:8px; margin-bottom:32px; }
  .total-row { display:flex; gap:80px; justify-content:flex-end; }
  .total-row span:first-child { color:#64748B; font-size:13px; }
  .total-row span:last-child { font-size:13px; font-weight:600; min-width:80px; text-align:right; }
  .total-final { border-top:2px solid #0e4e96; padding-top:10px; }
  .total-final span { font-size:18px; font-weight:800; color:#0e4e96; }
  .footer { border-top:1px solid #E2E8F0; padding-top:20px; display:flex; justify-content:space-between; align-items:center; }
  .footer p { font-size:11px; color:#94A3B8; }
  .footer .due { font-size:13px; font-weight:700; color:#1E293B; }
  .stripe-id { font-size:10px; color:#94A3B8; margin-top:4px; font-family:monospace; }
</style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="logo-area">
      <h1>JOBENG</h1>
      <p>Business Management Platform</p>
      <p style="font-size:11px;color:#94A3B8;margin-top:2px;">api.jobeng.ai · support@jobeng.ai</p>
    </div>
    <div class="invoice-meta">
      <div class="inv-num">${inv.invoice_number || inv.id}</div>
      <div class="inv-date">Issued: ${fmtDate(inv.issued_date || inv.created_at)}</div>
      <div class="badge ${inv.status === 'paid' ? '' : 'pending'}">${(inv.status || 'PENDING').toUpperCase()}</div>
    </div>
  </div>

  <div class="parties">
    <div class="party">
      <h3>From</h3>
      <strong>JOBENG Ltd</strong>
      <p>Platform Billing<br/>United Kingdom<br/>support@jobeng.ai</p>
    </div>
    <div class="party">
      <h3>Bill To</h3>
      <strong>${inv.client_name || company?.name || 'Business Client'}</strong>
      <p>${company?.email || inv.client_email || ''}<br/>${company?.phone || ''}</p>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>Type</th>
        <th>Period</th>
        <th style="text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${inv.description || 'JOBENG Subscription'}</td>
        <td>${inv.type || 'Subscription'}</td>
        <td>${fmtDate(inv.issued_date)} – ${fmtDate(inv.due_date)}</td>
        <td style="text-align:right;font-weight:600">${fmtCurrency(inv.amount)}</td>
      </tr>
    </tbody>
  </table>

  <div class="totals">
    <div class="total-row"><span>Subtotal</span><span>${fmtCurrency(inv.amount)}</span></div>
    <div class="total-row"><span>VAT (20%)</span><span>${fmtCurrency(parseFloat(inv.amount||0) * 0.2)}</span></div>
    <div class="total-row total-final"><span>Total Due</span><span>${fmtCurrency(parseFloat(inv.amount||0) * 1.2)}</span></div>
  </div>

  <div class="footer">
    <div>
      <p>Payment due: <span class="due">${fmtDate(inv.due_date)}</span></p>
      ${inv.stripe_payment_id ? `<p class="stripe-id">Stripe ref: ${inv.stripe_payment_id}</p>` : ''}
    </div>
    <p>Thank you for your business · JOBENG Ltd · Registered in England & Wales</p>
  </div>
</div>
</body>
</html>`;

// ────────────────────────────────────────────────────────────────────────────
export default function BusinessPaymentSystem({ navigation, route }) {
  const { subscription: routeSub, companyProfile: routeCompany, fromSubscription, fromUpgrade, initialTab } = route?.params || {};

  const [tab, setTab]                   = useState(initialTab || (fromSubscription ? 'subscription' : fromUpgrade ? 'sub_invoices' : 'overview'));
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('BusinessPaymentSystem');
  const [showAIChat, setShowAIChat] = useState(false);

  const [subscription, setSub]          = useState(routeSub || null);
  const [companyProfile, setCompany]    = useState(routeCompany || null);
  const [invoices, setInvoices]         = useState([]);     // company client invoices
  const [subInvoices, setSubInvoices]   = useState([]);     // subscription billing invoices
  const [transactions, setTransactions] = useState([]);
  const [paymentMethods, setMethods]    = useState([]);
  const [notifications, setNotifs]      = useState({ payment_due:true, invoice_generated:true, subscription_expiring:true, payment_failed:true });
  const [bizId, setBizId]               = useState(routeCompany?.id || null);

  // Modals
  const [showStripe, setShowStripe]     = useState(false);
  const [stripeScenario, setScenario]   = useState(null);
  const [showNewInvoice, setShowNewInv] = useState(false);
  const [showInvDetail, setShowInvDet] = useState(false);
  const [selInvoice, setSelInvoice]    = useState(null);
  const [showCancel, setShowCancel]    = useState(false);
  const [showAddCard, setShowAddCard]  = useState(false);

  // New invoice form
  const [invForm, setInvForm] = useState({ client:'', amount:'', description:'', due_date:'', type:'service' });

  const fadeAnim = useRef(new Animated.Value(0)).current;

  const TABS = [
    { id:'overview',     label:'Overview',     icon:'view-dashboard-outline' },
    { id:'subscription', label:'Subscription', icon:'crown-outline' },
    { id:'invoices',     label:'Client Invoices', icon:'file-document-outline' },
    { id:'sub_invoices', label:'Billing',         icon:'crown-outline' },
    { id:'transactions', label:'Payments',     icon:'credit-card-outline' },
    { id:'settings',     label:'Settings',     icon:'bell-outline' },
  ];

  useEffect(() => {
    boot();
    Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  }, []);

  // Refresh every time screen comes into focus (catches invoices created on previous screen)
  useFocusEffect(
    React.useCallback(() => {
      // Always resolve bizId from storage — may not be set in state yet on first focus
      const refresh = async () => {
        let cid = bizId;
        if (!cid) {
          cid = await AsyncStorage.getItem('company_id')
             || await AsyncStorage.getItem('company_id');
          const cp = await AsyncStorage.getItem('company_profile').catch(() => null);
          if (!cid && cp) cid = JSON.parse(cp)?.id;
          if (cid) setBizId(cid);
        }
        if (cid) {
          await Promise.all([
            fetchSubInvoices(cid),
            fetchInvoices(cid),
            fetchTransactions(cid),
          ]);
        }
      };
      refresh();
    }, [bizId])
  );

  const boot = async () => {
    setLoading(true);
    try {
      let cid = bizId;
      if (!cid) {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) { const p = JSON.parse(cp); cid = p.id; setBizId(p.id); setCompany(p); }
        else { cid = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id'); setBizId(cid); }
      }
      if (!companyProfile) {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) setCompany(JSON.parse(cp));
      }
      if (!subscription) {
        const cached = await AsyncStorage.getItem('business_subscription');
        if (cached) setSub(JSON.parse(cached));
      }
      if (cid) await Promise.all([fetchSubscription(cid), fetchInvoices(cid), fetchSubInvoices(cid), fetchTransactions(cid), fetchMethods(cid)]);
      // Check expiry on load
      checkExpiryReminder();
    } finally { setLoading(false); }
  };

  // Plan name mapping
  const PLAN_NAMES = {
    'free_trial':'Free Trial','single_starter':'Business Starter','single_professional':'Business Professional',
    'single_enterprise':'Business Enterprise','team_starter':'Team Starter','team_professional':'Team Professional',
    'team_enterprise':'Team Enterprise','multi_team':'Multi Team','multi_business':'Multi Business',
    'multi_enterprise':'Multi Enterprise','pro_basic':'Pro Basic','pro_plus':'Pro Plus',
    'learn_basic':'Learning Basic','learn_pro':'Learning Pro',
  };
  const PLAN_USERS = {
    'free_trial':1,'single_starter':1,'single_professional':5,'single_enterprise':10,
    'team_starter':5,'team_professional':15,'team_enterprise':50,
    'multi_team':25,'multi_business':50,'multi_enterprise':999,
  };

  const fetchSubscription = async (cid) => {
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/business-subscriptions?company_id=${cid}&status=active`, { headers: h });
      const d = await r.json();
      if (d.success && d.data?.length > 0) {
        const sub = d.data[0];
        // Normalize subscription data
        sub.price = parseFloat(sub.price_paid || sub.price || 0);
        sub.price_paid = sub.price;
        sub.plan_name = PLAN_NAMES[sub.plan_id] || sub.plan_name || sub.plan_id?.replace(/_/g,' ') || 'Business Plan';
        sub.max_users = sub.max_users || PLAN_USERS[sub.plan_id] || 1;
        sub.billing_cycle = sub.billing_cycle || 'monthly';
        // Also try to get plan details for features
        try {
          const pr = await fetch(`${API_BASE}/data/subscription_plans?plan_id=${sub.plan_id}&limit=1`, { headers: h });
          const pd = await pr.json();
          if (pd.success && pd.data?.[0]) {
            const plan = pd.data[0];
            if (!sub.features || !sub.features.length) sub.features = plan.features || [];
            if (!sub.max_users || sub.max_users <= 1) sub.max_users = plan.max_users || PLAN_USERS[sub.plan_id] || 50;
          }
        } catch {}
        console.log('[Payment] Subscription loaded:', sub.plan_name, '£' + sub.price_paid + '/' + sub.billing_cycle, 'users:' + sub.max_users);
        setSub(sub);
        await AsyncStorage.setItem('business_subscription', JSON.stringify(sub));
      }
    } catch {}
  };

  const fetchInvoices = async (cid) => {
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/data/financial_invoices?company_id=${parseInt(cid)||cid}`, { headers: h });
      const d = await r.json();
      if (d.success) setInvoices(d.data || []);
    } catch {}
  };

  const fetchSubInvoices = async (cid) => {
    if (!cid) return;
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/data/subscription_invoices?company_id=${parseInt(cid)||cid}`, { headers: h });
      if (!r.ok) return;
      const d = await r.json();
      if (d.success && d.data) {
        // Normalise — DB returns all numeric fields as strings
        const normalised = d.data.map(inv => ({
          ...inv,
          amount:     parseFloat(inv.amount     || 0),
          tax_amount: parseFloat(inv.tax_amount || 0),
          total:      parseFloat(inv.total      || 0),
          tax_rate:   parseFloat(inv.tax_rate   || 20),
        }));
        console.log(`[SubInvoices] Loaded ${normalised.length} for company ${cid}:`,
          normalised.map(i => `${i.invoice_number} £${i.total}`));
        setSubInvoices(normalised);
      }
    } catch (e) {
      console.warn('[fetchSubInvoices]', e.message);
    }
  };

  const fetchTransactions = async (cid) => {
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/data/transactions?company_id=${cid}`, { headers: h });
      const d = await r.json();
      if (d.success) setTransactions(d.data || []);
    } catch {}
  };

  const fetchMethods = async (cid) => {
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/data/payment_methods?company_id=${cid}`, { headers: h });
      const d = await r.json();
      if (d.success) setMethods(d.data || []);
    } catch {}
  };

  const checkExpiryReminder = async () => {
    const cached = await AsyncStorage.getItem('business_subscription').catch(() => null);
    if (!cached) return;
    const sub = JSON.parse(cached);
    const days = daysUntil(sub.end_date);
    if (days !== null && days <= 7 && days > 0 && notifications.subscription_expiring) {
      const shownKey = `expiry_alert_${sub.id}_${sub.end_date}`;
      const shown = await AsyncStorage.getItem(shownKey);
      if (!shown) {
        await AsyncStorage.setItem(shownKey, '1');
        setTimeout(() => Alert.alert(
          '⏰ Subscription Expiring Soon',
          `Your ${sub.plan_name || 'subscription'} expires in ${days} day${days !== 1 ? 's' : ''} on ${fmtDate(sub.end_date)}.\n\nRenew now to avoid interruption.`,
          [
            { text: 'Remind Later', style: 'cancel' },
            { text: 'Renew Now', onPress: () => navigation.navigate('BusinessSubscriptionSystem', { currentPlan: sub, companyProfile }) },
          ]
        ), 800);
      }
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    if (bizId) await Promise.all([fetchSubscription(bizId), fetchInvoices(bizId), fetchSubInvoices(bizId), fetchTransactions(bizId), fetchMethods(bizId)]);
    setRefreshing(false);
  };

  const createInvoice = async (paid = false, paymentRef = null) => {
    if (!invForm.client || !invForm.amount) {
      Alert.alert('Required', 'Client name and amount are required.');
      return;
    }
    try {
      const h = await getHeaders();
      const invAmount   = parseFloat(invForm.amount || 0);
      const invTax      = parseFloat((invAmount * 0.20).toFixed(2));
      const invTotal    = parseFloat((invAmount + invTax).toFixed(2));
      const invNum      = newInvoiceNum();
      const body = {
        invoice_number:  invNum,
        company_id:      parseInt(bizId) || null,
        client_name:     invForm.client,
        description:     invForm.description || 'Professional Services',
        status:          paid ? 'paid' : 'draft',
        issue_date:      new Date().toISOString().split('T')[0],
        due_date:        invForm.due_date || new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        subtotal:        invAmount,
        tax_rate:        20,
        tax_amount:      invTax,
        discount_rate:   0,
        discount_amount: 0,
        total:           invTotal,
        items:           JSON.stringify([{
          description: invForm.description || 'Professional Services',
          quantity:    1,
          unit_price:  invAmount,
          total:       invAmount,
        }]),
        notes:           `Type: ${invForm.type} | Company ID: ${bizId}`,
        reference:       invNum,
        payment_method:  paymentRef ? 'stripe' : null,
      };
      const r = await fetch(`${API_BASE}/data/financial_invoices`, { method:'POST', headers:h, body:JSON.stringify(body) });
      const d = await r.json();
      if (d.success) {
        await fetchInvoices(bizId);
        setShowNewInv(false);
        setInvForm({ client:'', amount:'', description:'', due_date:'', type:'service' });
        if (notifications.invoice_generated) {
          Alert.alert('✅ Invoice Created', `${body.invoice_number} for £${body.amount.toFixed(2)}`);
        }
        return d.data;
      }
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const handlePayInvoice = (inv) => {
    const scenario = {
      id:           `inv_${inv.id}`,
      type:         'invoice',
      name:         inv.description || 'Invoice Payment',
      amountInPence:Math.round(parseFloat(inv.amount || 0) * 100),
      currency:     'gbp',
      metadata:     { invoice_id: inv.id, company_id: bizId },
    };
    setScenario(scenario);
    setShowStripe(true);
  };

  const handleStripeSuccess = async (result) => {
    setShowStripe(false);
    try {
      const h = await getHeaders();
      const invId = result.scenario?.metadata?.invoice_id;
      if (invId) {
        await fetch(`${API_BASE}/data/financial_invoices/${invId}`, {
          method: 'PUT', headers: h,
          body: JSON.stringify({
            status:            'paid',
            paid_at:           new Date().toISOString(),
            payment_method:    'stripe',
            reference:         result.paymentIntent?.id || '',
          }),
        });
      }
      // Save transaction
      await fetch(`${API_BASE}/data/transactions`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          company_id:        bizId,
          type:              result.scenario.type,
          amount:            result.amount / 100,
          currency:          'GBP',
          status:            'completed',
          description:       result.scenario.name,
          stripe_payment_id: result.paymentIntent?.id,
          invoice_id:        invId,
          completed_at:      new Date().toISOString(),
        }),
      });
      await fetchInvoices(bizId);
      await fetchTransactions(bizId);
      Alert.alert('✅ Payment Successful', `£${(result.amount/100).toFixed(2)} processed.`);
    } catch {}
  };

  const exportInvoicePDF = async (inv) => {
    try {
      const html  = buildInvoiceHTML(inv, companyProfile);
      const { uri } = await Print.printToFileAsync({ html });
      await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:`Invoice ${inv.invoice_number}` });
    } catch (e) { Alert.alert('Error', 'Could not export PDF: ' + e.message); }
  };

  const printInvoice = async (inv) => {
    try {
      const html = buildInvoiceHTML(inv, companyProfile);
      await Print.printAsync({ html });
    } catch (e) { Alert.alert('Error', 'Could not print: ' + e.message); }
  };

  const sendInvoice = async (inv) => {
    const msg = `JOBENG Invoice ${inv.invoice_number || ''}\nAmount: £${parseFloat(inv.amount).toFixed(2)}\nDue: ${fmtDate(inv.due_date)}\nDescription: ${inv.description}\n\nPlease contact billing@jobeng.ai to pay online.`;
    await Share.share({ message: msg, title: `Invoice ${inv.invoice_number}` });
  };

  const markPaid = async (inv) => {
    try {
      const h = await getHeaders();
      await fetch(`${API_BASE}/data/financial_invoices/${inv.id}`, {
        method: 'PUT', headers: h,
        body: JSON.stringify({ status:'paid', paid_at: new Date().toISOString() }),
      });
      await fetchInvoices(bizId);
    } catch {}
  };

  // ── RENDER SECTIONS ─────────────────────────────────────────────────────────

  const renderExpiryBanner = () => {
    if (!subscription?.end_date) return null;
    const days = daysUntil(subscription.end_date);
    if (days === null || days > 14) return null;
    const urgent = days <= 3;
    return (
      <LinearGradient colors={urgent ? ['#EF4444','#DC2626'] : ['#F59E0B','#D97706']} style={styles.expiryBanner}>
        <MaterialCommunityIcons name={urgent ? 'alert' : 'clock-alert-outline'} size={18} color="#fff"/>
        <View style={{ flex:1, marginLeft:10 }}>
          <Text style={styles.expiryTitle}>{days <= 0 ? 'Subscription Expired' : `Expires in ${days} day${days !== 1?'s':''}`}</Text>
          <Text style={styles.expirySub}>Tap to renew and avoid service interruption</Text>
        </View>
        <TouchableOpacity style={styles.expiryBtn}
          onPress={() => navigation.navigate('BusinessSubscriptionSystem', { currentPlan:subscription, companyProfile })}>
          <Text style={styles.expiryBtnText}>Renew</Text>
        </TouchableOpacity>
      </LinearGradient>
    );
  };

  const renderOverview = () => {
    const totalPaid    = transactions.filter(t=>t.status==='completed').reduce((s,t)=>s+parseFloat(t.amount||0),0);
    const pendingInvs  = invoices.filter(i=>i.status==='pending' || i.status==='draft');
    const pendingTotal = pendingInvs.reduce((s,i)=>s+parseFloat(i.amount||0),0);
    const overdueInvs  = invoices.filter(i=>i.status==='overdue' || (i.status==='pending' && daysUntil(i.due_date) !== null && daysUntil(i.due_date) < 0));

    return (
      <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.primary}/>}>
        {/* Stats row */}
        <View style={styles.statsRow}>
          {[
            { label:'Total Paid',    value:fmtCurrency(totalPaid),    icon:'check-circle',     color:C.success },
            { label:'Pending',       value:fmtCurrency(pendingTotal),  icon:'clock-outline',    color:C.warning },
            { label:'Overdue',       value:String(overdueInvs.length), icon:'alert-circle',     color:C.danger },
            { label:'Invoices',      value:String(invoices.length),    icon:'file-document',    color:C.purple },
          ].map((s,i) => (
            <View key={i} style={styles.statCard}>
              <MaterialCommunityIcons name={s.icon} size={20} color={s.color}/>
              <Text style={[styles.statValue, { color:s.color }]}>{s.value}</Text>
              <Text style={styles.statLabel}>{s.label}</Text>
            </View>
          ))}
        </View>

        {/* Subscription card */}
        {subscription && (
          <View style={styles.overviewSubCard}>
            <LinearGradient colors={[C.primary, C.p2]} style={styles.overviewSubGrad} start={{x:0,y:0}} end={{x:1,y:1}}>
              <View style={styles.overviewSubTop}>
                <View>
                  <Text style={styles.overviewSubLabel}>CURRENT PLAN</Text>
                  <Text style={styles.overviewSubName}>{subscription.plan_name || subscription.plan_id}</Text>
                </View>
                <Text style={styles.overviewSubPrice}>£{parseFloat(subscription.price_paid || subscription.price || 0).toLocaleString('en-GB')}/{subscription.billing_cycle === 'yearly' ? 'yr' : 'mo'}</Text>
              </View>
              <View style={styles.overviewSubRow}>
                <View style={styles.overviewSubMeta}>
                  <MaterialCommunityIcons name="calendar-outline" size={13} color="rgba(255,255,255,0.7)"/>
                  <Text style={styles.overviewSubMetaTxt}>Renews {fmtDate(subscription.end_date)}</Text>
                </View>
                {subscription.max_users && (
                  <View style={styles.overviewSubMeta}>
                    <MaterialCommunityIcons name="account-group-outline" size={13} color="rgba(255,255,255,0.7)"/>
                    <Text style={styles.overviewSubMetaTxt}>{subscription.max_users >= 999 ? 'Unlimited' : subscription.max_users} users</Text>
                  </View>
                )}
              </View>
              <View style={styles.overviewSubActions}>
                <TouchableOpacity style={styles.overviewSubBtn} onPress={() => navigation.navigate('BusinessSubscriptionSystem', { currentPlan:subscription, companyProfile })}>
                  <Text style={styles.overviewSubBtnTxt}>Manage</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.overviewSubBtn, { backgroundColor:'rgba(255,255,255,0.15)' }]} onPress={() => setTab('invoices')}>
                  <Text style={styles.overviewSubBtnTxt}>View Invoices</Text>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>
        )}

        {/* Pending invoices alert */}
        {pendingInvs.length > 0 && (
          <TouchableOpacity style={styles.pendingAlert} onPress={() => setTab('invoices')}>
            <MaterialCommunityIcons name="file-alert-outline" size={20} color={C.warning}/>
            <View style={{ flex:1, marginLeft:10 }}>
              <Text style={styles.pendingAlertTitle}>{pendingInvs.length} Invoice{pendingInvs.length>1?'s':''} Awaiting Payment</Text>
              <Text style={styles.pendingAlertSub}>Total: {fmtCurrency(pendingTotal)} outstanding</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color={C.warning}/>
          </TouchableOpacity>
        )}

        {/* Recent transactions */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Recent Payments</Text>
            <TouchableOpacity onPress={() => setTab('transactions')}>
              <Text style={styles.sectionLink}>View All</Text>
            </TouchableOpacity>
          </View>
          {transactions.slice(0,4).map((t,i) => renderTxRow(t,i))}
          {transactions.length === 0 && <Text style={styles.emptyMsg}>No payment records yet</Text>}
        </View>
      </ScrollView>
    );
  };

  const renderTxRow = (t, i) => {
    const st = getStatusStyle(t.status);
    return (
      <View key={t.id||i} style={styles.txRow}>
        <View style={[styles.txIcon, { backgroundColor: t.type === 'subscription' ? '#EFF6FF' : '#F0FDF4' }]}>
          <MaterialCommunityIcons name={t.type==='subscription' ? 'crown-outline' : 'swap-horizontal'} size={18} color={t.type==='subscription' ? C.primary : C.success}/>
        </View>
        <View style={{ flex:1, marginLeft:12 }}>
          <Text style={styles.txDesc} numberOfLines={1}>{t.description || t.type}</Text>
          <Text style={styles.txDate}>{fmtDate(t.created_at || t.completed_at)}</Text>
        </View>
        <View style={{ alignItems:'flex-end', gap:4 }}>
          <Text style={[styles.txAmount, { color: t.amount < 0 ? C.danger : C.text }]}>{fmtCurrency(t.amount)}</Text>
          <View style={[styles.statusPill, { backgroundColor: st.bg }]}>
            <Text style={[styles.statusPillText, { color: st.text }]}>{t.status}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderSubscriptionTab = () => {
    if (!subscription) {
      return (
        <View style={styles.emptyCenter}>
          <MaterialCommunityIcons name="crown-outline" size={64} color={C.light}/>
          <Text style={styles.emptyTitle}>No Active Plan</Text>
          <TouchableOpacity style={styles.emptyBtn}
            onPress={() => navigation.navigate('BusinessSubscriptionSystem', { companyProfile })}>
            <Text style={styles.emptyBtnText}>Choose a Plan</Text>
          </TouchableOpacity>
        </View>
      );
    }
    const days    = daysUntil(subscription.end_date);
    const features = Array.isArray(subscription.features) ? subscription.features
                   : Array.isArray(subscription.plan_features) ? subscription.plan_features : [];
    const subTx   = transactions.filter(t => t.type === 'subscription' || t.subscription_id === subscription.id);

    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <LinearGradient colors={[C.primary, C.p2]} style={styles.subHero}>
          <View style={styles.subHeroTop}>
            <MaterialCommunityIcons name="crown" size={32} color={C.gold}/>
            <View style={{ flex:1, marginLeft:12 }}>
              <Text style={styles.subHeroPlan}>{subscription.plan_name || subscription.plan_id}</Text>
              <Text style={styles.subHeroPrice}>£{parseFloat(subscription.price_paid || subscription.price || 0).toLocaleString('en-GB')}/{subscription.billing_cycle === 'yearly' ? 'year' : 'month'}</Text>
            </View>
            <View style={[styles.statusPill, { backgroundColor: subscription.status === 'active' ? C.success : C.warning }]}>
              <Text style={[styles.statusPillText, { color:'#fff' }]}>{(subscription.status || 'active').toUpperCase()}</Text>
            </View>
          </View>
          {days !== null && days <= 30 && (
            <View style={styles.subHeroExpiry}>
              <MaterialCommunityIcons name="clock-outline" size={14} color={days <= 7 ? '#FCA5A5' : 'rgba(255,255,255,0.75)'}/>
              <Text style={[styles.subHeroExpiryText, days <= 7 && { color:'#FCA5A5' }]}>
                <Text>{days <= 0 ? 'Expired' : `${days} days remaining`} · Renews {fmtDate(subscription.end_date)}</Text>
              </Text>
            </View>
          )}
        </LinearGradient>

        {/* Meta grid */}
        <View style={styles.metaGrid}>
          {[
            { label:'Plan Type',   value: subscription.plan_name || subscription.plan_type?.replace('_',' ') || 'Standard' },
            { label:'Max Users',   value: subscription.max_users >= 999 ? 'Unlimited' : String(subscription.max_users || 50) },
            { label:'Billing',     value: (subscription.billing_cycle || 'monthly').charAt(0).toUpperCase() + (subscription.billing_cycle || 'monthly').slice(1) },
            { label:'Start Date',  value: fmtDate(subscription.start_date) },
            { label:'Renews',      value: fmtDate(subscription.end_date) },
            { label:'Auto-Renew',  value: subscription.auto_renew !== false ? 'Yes' : 'No' },
          ].map((m,i) => (
            <View key={i} style={styles.metaItem}>
              <Text style={styles.metaKey}>{m.label}</Text>
              <Text style={styles.metaVal}>{m.value}</Text>
            </View>
          ))}
        </View>

        {features.length > 0 && (
          <View style={styles.subFeatures}>
            <Text style={styles.sectionTitle}>Plan Features</Text>
            <View style={{ gap:8, marginTop:12 }}>
              {features.map((f,i) => (
                <View key={i} style={styles.featureRow2}>
                  <View style={styles.featureCheck}><MaterialCommunityIcons name="check" size={12} color={C.success}/></View>
                  <Text style={styles.featureText2}>{f}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Actions */}
        <View style={styles.subActions}>
          <TouchableOpacity style={styles.subActBtn}
            onPress={() => navigation.navigate('BusinessSubscriptionSystem', { currentPlan:subscription, companyProfile })}>
            <MaterialCommunityIcons name="swap-horizontal" size={18} color={C.primary}/>
            <Text style={styles.subActBtnText}>Change Plan</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.subActBtn, { borderColor:C.danger+'44' }]} onPress={() => setShowCancel(true)}>
            <MaterialCommunityIcons name="close-circle-outline" size={18} color={C.danger}/>
            <Text style={[styles.subActBtnText, { color:C.danger }]}>Cancel Plan</Text>
          </TouchableOpacity>
        </View>

        {/* Billing history */}
        {subTx.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Billing History</Text>
            {subTx.map((t,i) => renderTxRow(t,i))}
          </View>
        )}
      </ScrollView>
    );
  };

  const renderInvoices = () => (
    <View style={{ flex:1 }}>
      <View style={styles.invoiceToolbar}>
        <Text style={styles.sectionTitle}>Invoices ({invoices.length})</Text>
        <TouchableOpacity style={styles.newInvBtn} onPress={() => setShowNewInv(true)}>
          <MaterialCommunityIcons name="plus" size={16} color="#fff"/>
          <Text style={styles.newInvBtnText}>New Invoice</Text>
        </TouchableOpacity>
      </View>
      {invoices.length === 0 ? (
        <View style={styles.emptyCenter}>
          <MaterialCommunityIcons name="file-document-outline" size={56} color={C.light}/>
          <Text style={styles.emptyTitle}>No Invoices</Text>
          <Text style={styles.emptySub}>Invoices are generated automatically after payment</Text>
        </View>
      ) : (
        <FlatList
          data={invoices}
          keyExtractor={(item,i) => String(item.id||i)}
          contentContainerStyle={{ padding:16, gap:12 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.primary}/>}
          renderItem={({ item: inv }) => {
            const st    = getStatusStyle(inv.status);
            const overdue = inv.status === 'pending' && daysUntil(inv.due_date) !== null && daysUntil(inv.due_date) < 0;
            return (
              <TouchableOpacity style={[styles.invCard, overdue && styles.invCardOverdue]}
                onPress={() => { setSelInvoice(inv); setShowInvDet(true); }}>
                <View style={styles.invCardTop}>
                  <View>
                    <Text style={styles.invNum}>{inv.invoice_number || `INV-${inv.id}`}</Text>
                    <Text style={styles.invClient} numberOfLines={1}>{inv.client_name || 'Business Client'}</Text>
                  </View>
                  <View style={{ alignItems:'flex-end', gap:6 }}>
                    <Text style={styles.invAmount}>{fmtCurrency(inv.amount)}</Text>
                    <View style={[styles.statusPill, { backgroundColor: overdue ? '#FEE2E2' : st.bg }]}>
                      <Text style={[styles.statusPillText, { color: overdue ? '#991B1B' : st.text }]}>
                        {overdue ? 'OVERDUE' : (inv.status || 'pending').toUpperCase()}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.invMeta}>
                  <Text style={styles.invMetaTxt}>Due: {fmtDate(inv.due_date)}</Text>
                  {inv.description && <Text style={styles.invMetaTxt} numberOfLines={1}>{inv.description}</Text>}
                </View>
                <View style={styles.invActions}>
                  <TouchableOpacity style={styles.invActionBtn} onPress={() => exportInvoicePDF(inv)}>
                    <MaterialCommunityIcons name="file-pdf-box" size={15} color={C.danger}/>
                    <Text style={styles.invActionTxt}>PDF</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.invActionBtn} onPress={() => sendInvoice(inv)}>
                    <MaterialCommunityIcons name="send-outline" size={15} color={C.primary}/>
                    <Text style={styles.invActionTxt}>Send</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.invActionBtn} onPress={() => printInvoice(inv)}>
                    <MaterialCommunityIcons name="printer-outline" size={15} color={C.sub}/>
                    <Text style={styles.invActionTxt}>Print</Text>
                  </TouchableOpacity>
                  {inv.status !== 'paid' && (
                    <TouchableOpacity style={[styles.invActionBtn, { backgroundColor:'#D1FAE5' }]} onPress={() => handlePayInvoice(inv)}>
                      <MaterialCommunityIcons name="credit-card-outline" size={15} color={C.success}/>
                      <Text style={[styles.invActionTxt, { color:C.success }]}>Pay Now</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </TouchableOpacity>
            );
          }}
        />
      )}
    </View>
  );

  const renderSubInvoices = () => {
    const pending = subInvoices.filter(i => i.status === 'pending');
    const paid    = subInvoices.filter(i => i.status === 'paid');
    const fmtPlan = (inv) => inv.plan_name || inv.plan_id || 'Subscription';
    return (
      <View style={{ flex:1 }}>
        <View style={styles.invoiceToolbar}>
          <Text style={styles.sectionTitle}>Subscription Billing ({subInvoices.length})</Text>
          {pending.length > 0 && (
            <View style={[styles.statusPill, { backgroundColor:'#FEF3C7' }]}>
              <Text style={[styles.statusPillText, { color:'#92400E' }]}>{pending.length} PENDING</Text>
            </View>
          )}
        </View>
        {subInvoices.length === 0 ? (
          <View style={styles.emptyCenter}>
            <MaterialCommunityIcons name="crown-outline" size={56} color={C.light}/>
            <Text style={styles.emptyTitle}>No Billing Invoices</Text>
            <Text style={styles.emptySub}>Invoices are generated automatically when you subscribe or upgrade</Text>
          </View>
        ) : (
          <FlatList
            data={subInvoices}
            keyExtractor={(item,i) => String(item.id||i)}
            contentContainerStyle={{ padding:16, gap:12 }}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.primary}/>}
            renderItem={({ item: inv }) => {
              const st = getStatusStyle(inv.status);
              return (
                <View style={styles.invCard}>
                  <View style={styles.invCardTop}>
                    <View style={{ flex:1 }}>
                      <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:4 }}>
                        <MaterialCommunityIcons name="crown" size={14} color={C.gold}/>
                        <Text style={[styles.invNum, { color:C.purple }]}>{inv.invoice_number}</Text>
                      </View>
                      <Text style={styles.invClient}>{fmtPlan(inv)}</Text>
                      <Text style={[styles.invMetaTxt, { marginTop:2 }]}>{inv.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} · {inv.plan_id}</Text>
                    </View>
                    <View style={{ alignItems:'flex-end', gap:6 }}>
                      <View>
                        <Text style={styles.invAmount}>
                          {inv.total > 0 ? fmtCurrency(inv.total) : fmtCurrency(inv.amount * 1.2)}
                        </Text>
                        <Text style={[styles.invMetaTxt, { textAlign:'right', marginTop:2 }]}>incl. VAT {inv.tax_rate || 20}%</Text>
                      </View>
                      <View style={[styles.statusPill, { backgroundColor: st.bg }]}>
                        <Text style={[styles.statusPillText, { color: st.text }]}>{(inv.status||'pending').toUpperCase()}</Text>
                      </View>
                    </View>
                  </View>
                  <View style={styles.invMeta}>
                    <Text style={styles.invMetaTxt}>Issued: {fmtDate(inv.issue_date)}</Text>
                    <Text style={styles.invMetaTxt}>Due: {fmtDate(inv.due_date)}</Text>
                  </View>
                  {/* Breakdown */}
                  <View style={{ flexDirection:'row', gap:12, paddingVertical:8, borderTopWidth:1, borderTopColor:C.border, marginTop:8 }}>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:10, color:C.light, textTransform:'uppercase' }}>Plan Cost</Text>
                      <Text style={{ fontSize:13, fontWeight:'600', color:C.text }}>{fmtCurrency(inv.amount)}</Text>
                    </View>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:10, color:C.light, textTransform:'uppercase' }}>VAT ({inv.tax_rate}%)</Text>
                      <Text style={{ fontSize:13, fontWeight:'600', color:C.text }}>{fmtCurrency(inv.tax_amount)}</Text>
                    </View>
                    <View style={{ flex:1 }}>
                      <Text style={{ fontSize:10, color:C.light, textTransform:'uppercase' }}>Total</Text>
                      <Text style={{ fontSize:14, fontWeight:'800', color:C.primary }}>{fmtCurrency(inv.total)}</Text>
                    </View>
                  </View>
                  {/* Actions */}
                  <View style={styles.invActions}>
                    <TouchableOpacity style={styles.invActionBtn} onPress={() => exportInvoicePDF(inv)}>
                      <MaterialCommunityIcons name="file-pdf-box" size={15} color={C.danger}/>
                      <Text style={styles.invActionTxt}>PDF</Text>
                    </TouchableOpacity>
                    {inv.status !== 'paid' && (
                      <TouchableOpacity style={[styles.invActionBtn, { backgroundColor:'#D1FAE5' }]}
                        onPress={() => {
                          const scenario = {
                            id:            `sub_inv_${inv.id}`,
                            type:          'subscription',
                            name:          `${fmtPlan(inv)} — ${inv.billing_cycle} Billing`,
                            amountInPence: Math.round(parseFloat(inv.total || inv.amount || 0) * 100),
                            currency:      'gbp',
                            metadata:      { sub_invoice_id: inv.id, company_id: bizId },
                          };
                          setScenario(scenario);
                          setShowStripe(true);
                        }}>
                        <MaterialCommunityIcons name="credit-card-outline" size={15} color={C.success}/>
                        <Text style={[styles.invActionTxt, { color:C.success }]}>Pay Now</Text>
                      </TouchableOpacity>
                    )}
                    {inv.stripe_payment_id && (
                      <View style={styles.invActionBtn}>
                        <MaterialCommunityIcons name="check-circle" size={15} color={C.success}/>
                        <Text style={[styles.invActionTxt, { color:C.success }]}>Paid</Text>
                      </View>
                    )}
                  </View>
                </View>
              );
            }}
          />
        )}
      </View>
    );
  };

  const renderTransactions = () => (
    <FlatList
      data={transactions}
      keyExtractor={(item,i) => String(item.id||i)}
      contentContainerStyle={{ padding:16, gap:10 }}
      ListHeaderComponent={<Text style={[styles.sectionTitle, { marginBottom:8 }]}>All Transactions ({transactions.length})</Text>}
      ListEmptyComponent={<View style={styles.emptyCenter}><MaterialCommunityIcons name="credit-card-outline" size={48} color={C.light}/><Text style={styles.emptyTitle}>No Transactions</Text></View>}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.primary}/>}
      renderItem={({ item: t }) => (
        <View style={styles.txCard}>
          {renderTxRow(t, t.id)}
          {t.stripe_payment_id && <Text style={styles.stripeRef}>Stripe: {t.stripe_payment_id}</Text>}
        </View>
      )}
    />
  );

  const renderSettings = () => (
    <ScrollView contentContainerStyle={{ padding:16 }}>
      <Text style={styles.sectionTitle}>Notification Settings</Text>
      <Text style={styles.settingsSubtitle}>Choose what alerts you receive</Text>
      {[
        { key:'subscription_expiring', label:'Subscription Expiry Reminder', sub:'Alert 7 days before renewal', icon:'clock-alert-outline' },
        { key:'payment_due',           label:'Payment Due Alerts',           sub:'Remind before invoice due date', icon:'bell-outline' },
        { key:'invoice_generated',     label:'Invoice Generated',            sub:'Notify when invoices are created', icon:'file-document-outline' },
        { key:'payment_failed',        label:'Payment Failed Alerts',        sub:'Notify on declined payments', icon:'alert-circle-outline' },
      ].map(n => (
        <View key={n.key} style={styles.settingRow}>
          <View style={[styles.settingIcon, { backgroundColor:'#EFF6FF' }]}>
            <MaterialCommunityIcons name={n.icon} size={18} color={C.primary}/>
          </View>
          <View style={{ flex:1, marginLeft:12 }}>
            <Text style={styles.settingLabel}>{n.label}</Text>
            <Text style={styles.settingSub}>{n.sub}</Text>
          </View>
          <Switch
            value={notifications[n.key]}
            onValueChange={v => setNotifs(prev => ({ ...prev, [n.key]: v }))}
            trackColor={{ true: C.primary }}
            thumbColor="#fff"
          />
        </View>
      ))}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Danger Zone</Text>
        <TouchableOpacity style={styles.dangerBtn} onPress={() => setShowCancel(true)}>
          <MaterialCommunityIcons name="close-circle-outline" size={18} color={C.danger}/>
          <Text style={styles.dangerBtnText}>Cancel Subscription</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  // ── LOADING ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={C.primary}/>
          <Text style={styles.loadingText}>Loading payment center...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={C.primary}/>

      {/* Header */}
      <LinearGradient colors={[C.primary, C.p2]} style={styles.header}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation?.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#fff"/>
        </TouchableOpacity>
        <View style={{ flex:1 }}>
          <Text style={styles.headerTitle}>Payment Center</Text>
          <Text style={styles.headerSub}>{companyProfile?.name || 'JOBENG Business'}</Text>
        </View>
        <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowNewInv(true)}>
          <MaterialCommunityIcons name="file-plus-outline" size={22} color="#fff"/>
        </TouchableOpacity>
      </LinearGradient>

      {renderExpiryBanner()}

      {/* Tab Bar */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabScrollBar}>
        <View style={styles.tabBarInner}>
          {TABS.map(t => (
            <TouchableOpacity key={t.id} style={[styles.tabItem, tab===t.id && styles.tabItemActive]} onPress={() => setTab(t.id)}>
              <MaterialCommunityIcons name={t.icon} size={16} color={tab===t.id ? C.primary : C.light}/>
              <Text style={[styles.tabLabel, tab===t.id && styles.tabLabelActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      <Animated.View style={{ flex:1, opacity: fadeAnim }}>
        {tab === 'overview'     && renderOverview()}
        {tab === 'subscription' && renderSubscriptionTab()}
        {tab === 'invoices'     && renderInvoices()}
        {tab === 'sub_invoices' && renderSubInvoices()}
        {tab === 'transactions' && renderTransactions()}
        {tab === 'settings'     && renderSettings()}
      </Animated.View>

      {/* ── NEW INVOICE MODAL ── */}
      <Modal visible={showNewInvoice} animationType="slide" onRequestClose={() => setShowNewInv(false)}>
        <SafeAreaView style={{ flex:1, backgroundColor: C.bg }}>
          <LinearGradient colors={[C.primary, C.p2]} style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowNewInv(false)}>
              <Ionicons name="close" size={24} color="#fff"/>
            </TouchableOpacity>
            <Text style={styles.modalHeaderTitle}>New Invoice</Text>
            <View style={{ width:24 }}/>
          </LinearGradient>
          <ScrollView contentContainerStyle={{ padding:20, gap:14 }}>
            {[
              { label:'Client Name *',   key:'client',      placeholder:'e.g. Acme Engineering Ltd', kb:'default' },
              { label:'Amount (£) *',    key:'amount',      placeholder:'0.00', kb:'numeric' },
              { label:'Description',     key:'description', placeholder:'e.g. Consultancy Services – March 2026', kb:'default' },
              { label:'Due Date',        key:'due_date',    placeholder:'YYYY-MM-DD', kb:'default' },
            ].map(f => (
              <View key={f.key}>
                <Text style={styles.formLabel}>{f.label}</Text>
                <TextInput
                  style={styles.formInput}
                  value={invForm[f.key]}
                  onChangeText={v => setInvForm(p => ({ ...p, [f.key]: v }))}
                  placeholder={f.placeholder}
                  placeholderTextColor={C.light}
                  keyboardType={f.kb}
                />
              </View>
            ))}
            <Text style={styles.formLabel}>Type</Text>
            <View style={styles.typeRow}>
              {['service','subscription','project','other'].map(ty => (
                <TouchableOpacity
                  key={ty}
                  style={[styles.typeChip, invForm.type === ty && styles.typeChipActive]}
                  onPress={() => setInvForm(p => ({ ...p, type:ty }))}
                >
                  <Text style={[styles.typeChipText, invForm.type === ty && styles.typeChipTextActive]}>{ty.charAt(0).toUpperCase()+ty.slice(1)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
          <View style={styles.modalFooter}>
            <TouchableOpacity style={styles.footerBtnSec} onPress={() => setShowNewInv(false)}>
              <Text style={styles.footerBtnSecTxt}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.footerBtnPri} onPress={() => createInvoice()}>
              <MaterialCommunityIcons name="file-check-outline" size={18} color="#fff"/>
              <Text style={styles.footerBtnPriTxt}>Create Invoice</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>

      {/* ── INVOICE DETAIL MODAL ── */}
      <Modal visible={showInvDetail && !!selInvoice} animationType="slide" onRequestClose={() => setShowInvDet(false)}>
        <SafeAreaView style={{ flex:1, backgroundColor: C.bg }}>
          <LinearGradient colors={[C.primary, C.p2]} style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowInvDet(false)}>
              <Ionicons name="arrow-back" size={24} color="#fff"/>
            </TouchableOpacity>
            <Text style={styles.modalHeaderTitle}>{selInvoice?.invoice_number || 'Invoice'}</Text>
            <TouchableOpacity onPress={() => selInvoice && exportInvoicePDF(selInvoice)}>
              <MaterialCommunityIcons name="file-pdf-box" size={24} color="#fff"/>
            </TouchableOpacity>
          </LinearGradient>
          {selInvoice && (
            <ScrollView contentContainerStyle={{ padding:20 }}>
              <View style={styles.invDetailCard}>
                <View style={styles.invDetailHeader}>
                  <View>
                    <Text style={styles.invDetailNum}>{selInvoice.invoice_number}</Text>
                    <Text style={styles.invDetailClient}>{selInvoice.client_name}</Text>
                  </View>
                  <Text style={styles.invDetailAmount}>{fmtCurrency(selInvoice.amount)}</Text>
                </View>
                {[
                  { label:'Status',      value: selInvoice.status },
                  { label:'Type',        value: selInvoice.type || 'Service' },
                  { label:'Issued',      value: fmtDate(selInvoice.issued_date || selInvoice.created_at) },
                  { label:'Due Date',    value: fmtDate(selInvoice.due_date) },
                  { label:'Paid At',     value: selInvoice.paid_at ? fmtDate(selInvoice.paid_at) : '—' },
                  { label:'Description', value: selInvoice.description || '—' },
                ].map((r,i) => (
                  <View key={i} style={styles.invDetailRow}>
                    <Text style={styles.invDetailKey}>{r.label}</Text>
                    <Text style={styles.invDetailVal}>{r.value}</Text>
                  </View>
                ))}
                {selInvoice.stripe_payment_id && (
                  <View style={styles.invDetailRow}>
                    <Text style={styles.invDetailKey}>Stripe Ref</Text>
                    <Text style={[styles.invDetailVal, { fontFamily:'monospace', fontSize:11 }]}>{selInvoice.stripe_payment_id}</Text>
                  </View>
                )}
              </View>
            </ScrollView>
          )}
          <View style={styles.modalFooter}>
            {selInvoice?.status !== 'paid' && (
              <TouchableOpacity style={styles.footerBtnPri} onPress={() => { setShowInvDet(false); setTimeout(() => handlePayInvoice(selInvoice), 300); }}>
                <MaterialCommunityIcons name="credit-card-outline" size={18} color="#fff"/>
                <Text style={styles.footerBtnPriTxt}>Pay Now</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.footerBtnSec} onPress={() => selInvoice && sendInvoice(selInvoice)}>
              <MaterialCommunityIcons name="send-outline" size={18} color={C.primary}/>
              <Text style={[styles.footerBtnSecTxt, { color:C.primary }]}>Send</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.footerBtnSec} onPress={() => selInvoice && printInvoice(selInvoice)}>
              <MaterialCommunityIcons name="printer-outline" size={18} color={C.sub}/>
              <Text style={styles.footerBtnSecTxt}>Print</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>

      {/* ── CANCEL MODAL ── */}
      <Modal visible={showCancel} animationType="slide" transparent onRequestClose={() => setShowCancel(false)}>
        <View style={styles.overlayModal}>
          <View style={styles.cancelModal}>
            <MaterialCommunityIcons name="alert-circle-outline" size={48} color={C.danger}/>
            <Text style={styles.cancelTitle}>Cancel Subscription?</Text>
            <Text style={styles.cancelBody}>
              Your plan will remain active until {fmtDate(subscription?.end_date)}. You won't be charged again after cancellation.
            </Text>
            <View style={styles.cancelActions}>
              <TouchableOpacity style={styles.footerBtnSec} onPress={() => setShowCancel(false)}>
                <Text style={styles.footerBtnSecTxt}>Keep Plan</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.footerBtnPri, { backgroundColor: C.danger }]} onPress={async () => {
                setShowCancel(false);
                try {
                  const h = await getHeaders();
                  if (subscription?.id) {
                    await fetch(`${API_BASE}/business-subscriptions/${subscription.id}`, {
                      method:'PUT', headers:h, body: JSON.stringify({ status:'cancelled', cancelled_at: new Date().toISOString() }),
                    });
                  }
                  setSub(prev => ({ ...prev, status:'cancelled' }));
                  Alert.alert('Cancelled', 'Your subscription has been cancelled.');
                } catch {}
              }}>
                <Text style={styles.footerBtnPriTxt}>Confirm Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── STRIPE PAYMENT ── */}
      {stripeScenario && (
        <Modal visible={showStripe} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowStripe(false)}>
          <StripePaymentIntegration
            scenario={stripeScenario}
            onPaymentSuccess={handleStripeSuccess}
            onPaymentFailure={(e) => { Alert.alert('Payment Failed', e.message); setShowStripe(false); }}
            onCancel={() => setShowStripe(false)}
          />
        </Modal>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea:             { flex:1, backgroundColor: C.bg },
  loadingContainer:     { flex:1, justifyContent:'center', alignItems:'center', gap:12 },
  loadingText:          { color:C.sub, fontSize:14 },
  header:               { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:14, paddingTop:20, gap:12 },
  backBtn:              { width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  headerTitle:          { color:'#fff', fontSize:17, fontWeight:'700' },
  headerSub:            { color:'rgba(255,255,255,0.75)', fontSize:12, marginTop:1 },
  headerIconBtn:        { width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  expiryBanner:         { flexDirection:'row', alignItems:'center', padding:12, paddingHorizontal:16 },
  expiryTitle:          { color:'#fff', fontWeight:'700', fontSize:13 },
  expirySub:            { color:'rgba(255,255,255,0.85)', fontSize:11, marginTop:1 },
  expiryBtn:            { backgroundColor:'rgba(255,255,255,0.25)', paddingHorizontal:14, paddingVertical:6, borderRadius:16 },
  expiryBtnText:        { color:'#fff', fontWeight:'700', fontSize:12 },
  tabScrollBar:         { backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:C.border, maxHeight:56, flexGrow:0 },
  tabBarInner:          { flexDirection:'row', paddingHorizontal:8 },
  tabItem:              { alignItems:'center', paddingVertical:10, paddingHorizontal:14, gap:3, borderBottomWidth:2, borderBottomColor:'transparent' },
  tabItemActive:        { borderBottomColor: C.primary },
  tabLabel:             { fontSize:11, color:C.light, fontWeight:'600' },
  tabLabelActive:       { color: C.primary },
  statsRow:             { flexDirection:'row', padding:16, gap:10 },
  statCard:             { flex:1, backgroundColor:'#fff', borderRadius:14, padding:12, alignItems:'center', gap:5, shadowColor:'#000', shadowOpacity:0.05, shadowRadius:6, elevation:2 },
  statValue:            { fontSize:15, fontWeight:'800' },
  statLabel:            { fontSize:10, color:C.sub, textAlign:'center' },
  overviewSubCard:      { marginHorizontal:16, marginBottom:12, borderRadius:18, overflow:'hidden' },
  overviewSubGrad:      { padding:18 },
  overviewSubTop:       { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 },
  overviewSubLabel:     { color:'rgba(255,255,255,0.7)', fontSize:9, fontWeight:'700', letterSpacing:1.2, textTransform:'uppercase' },
  overviewSubName:      { color:'#fff', fontSize:17, fontWeight:'800', marginTop:3 },
  overviewSubPrice:     { color:'#fff', fontSize:18, fontWeight:'800' },
  overviewSubRow:       { flexDirection:'row', gap:16, marginBottom:14 },
  overviewSubMeta:      { flexDirection:'row', alignItems:'center', gap:5 },
  overviewSubMetaTxt:   { color:'rgba(255,255,255,0.75)', fontSize:11 },
  overviewSubActions:   { flexDirection:'row', gap:10 },
  overviewSubBtn:       { flex:1, backgroundColor:'rgba(255,255,255,0.2)', paddingVertical:8, borderRadius:10, alignItems:'center' },
  overviewSubBtnTxt:    { color:'#fff', fontWeight:'700', fontSize:12 },
  pendingAlert:         { flexDirection:'row', alignItems:'center', backgroundColor:'#FFFBEB', marginHorizontal:16, marginBottom:12, padding:14, borderRadius:14, borderWidth:1, borderColor:'#FDE68A' },
  pendingAlertTitle:    { fontSize:13, fontWeight:'700', color:'#92400E' },
  pendingAlertSub:      { fontSize:11, color:'#B45309', marginTop:2 },
  section:              { backgroundColor:'#fff', marginHorizontal:16, marginBottom:12, borderRadius:16, padding:16 },
  sectionHeader:        { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:12 },
  sectionTitle:         { fontSize:15, fontWeight:'700', color:C.text },
  sectionLink:          { fontSize:13, color:C.primary, fontWeight:'600' },
  settingsSubtitle:     { fontSize:13, color:C.sub, marginTop:4, marginBottom:20 },
  settingRow:           { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:10, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:6, elevation:2 },
  settingIcon:          { width:38, height:38, borderRadius:19, justifyContent:'center', alignItems:'center' },
  settingLabel:         { fontSize:14, fontWeight:'600', color:C.text },
  settingSub:           { fontSize:12, color:C.sub, marginTop:2 },
  dangerBtn:            { flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#FEF2F2', padding:14, borderRadius:12, borderWidth:1, borderColor:'#FCA5A5', marginTop:12 },
  dangerBtnText:        { color:C.danger, fontWeight:'700', fontSize:14 },
  txRow:                { flexDirection:'row', alignItems:'center', paddingVertical:10, borderBottomWidth:1, borderBottomColor:C.border },
  txIcon:               { width:38, height:38, borderRadius:19, justifyContent:'center', alignItems:'center' },
  txDesc:               { fontSize:13, fontWeight:'600', color:C.text },
  txDate:               { fontSize:11, color:C.sub, marginTop:2 },
  txAmount:             { fontSize:14, fontWeight:'700' },
  txCard:               { backgroundColor:'#fff', borderRadius:14, padding:14, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:6, elevation:2 },
  stripeRef:            { fontSize:10, color:C.light, fontFamily:'monospace', marginTop:8, paddingTop:8, borderTopWidth:1, borderTopColor:C.border },
  statusPill:           { paddingHorizontal:8, paddingVertical:3, borderRadius:10 },
  statusPillText:       { fontSize:9, fontWeight:'700', letterSpacing:0.5 },
  emptyMsg:             { color:C.light, textAlign:'center', paddingVertical:16, fontSize:13 },
  emptyCenter:          { flex:1, justifyContent:'center', alignItems:'center', gap:12, padding:40 },
  emptyTitle:           { fontSize:16, fontWeight:'700', color:C.text },
  emptySub:             { fontSize:13, color:C.sub, textAlign:'center' },
  emptyBtn:             { backgroundColor:C.primary, paddingHorizontal:24, paddingVertical:12, borderRadius:12 },
  emptyBtnText:         { color:'#fff', fontWeight:'700' },
  invoiceToolbar:       { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:16, paddingBottom:8, backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:C.border },
  newInvBtn:            { flexDirection:'row', alignItems:'center', gap:6, backgroundColor:C.primary, paddingHorizontal:14, paddingVertical:8, borderRadius:12 },
  newInvBtnText:        { color:'#fff', fontWeight:'700', fontSize:13 },
  invCard:              { backgroundColor:'#fff', borderRadius:16, padding:16, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:8, elevation:3 },
  invCardOverdue:       { borderWidth:1.5, borderColor:'#FCA5A5' },
  invCardTop:           { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 },
  invNum:               { fontSize:14, fontWeight:'800', color:C.primary },
  invClient:            { fontSize:12, color:C.sub, marginTop:3, maxWidth:180 },
  invAmount:            { fontSize:18, fontWeight:'900', color:C.text },
  invMeta:              { flexDirection:'row', gap:12, marginBottom:12 },
  invMetaTxt:           { fontSize:11, color:C.sub },
  invActions:           { flexDirection:'row', gap:8, flexWrap:'wrap' },
  invActionBtn:         { flexDirection:'row', alignItems:'center', gap:5, backgroundColor:C.bg, paddingHorizontal:12, paddingVertical:7, borderRadius:10 },
  invActionTxt:         { fontSize:12, color:C.sub, fontWeight:'600' },
  subHero:              { margin:16, borderRadius:20, padding:20, overflow:'hidden' },
  subHeroTop:           { flexDirection:'row', alignItems:'center', marginBottom:14 },
  subHeroPlan:          { color:'#fff', fontSize:20, fontWeight:'800' },
  subHeroPrice:         { color:'rgba(255,255,255,0.8)', fontSize:13, marginTop:3 },
  subHeroExpiry:        { flexDirection:'row', alignItems:'center', gap:6, backgroundColor:'rgba(0,0,0,0.15)', padding:10, borderRadius:10 },
  subHeroExpiryText:    { color:'rgba(255,255,255,0.8)', fontSize:12 },
  metaGrid:             { flexDirection:'row', flexWrap:'wrap', marginHorizontal:16, marginBottom:12, gap:10 },
  metaItem:             { width:(width-52)/2-5, backgroundColor:'#fff', borderRadius:12, padding:12, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:5, elevation:2 },
  metaKey:              { fontSize:11, color:C.sub, textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 },
  metaVal:              { fontSize:14, fontWeight:'700', color:C.text },
  subFeatures:          { backgroundColor:'#fff', marginHorizontal:16, marginBottom:12, borderRadius:16, padding:16 },
  featureRow2:          { flexDirection:'row', alignItems:'center', gap:10 },
  featureCheck:         { width:20, height:20, borderRadius:10, backgroundColor:'#D1FAE5', justifyContent:'center', alignItems:'center' },
  featureText2:         { fontSize:13, color:C.text, flex:1 },
  subActions:           { flexDirection:'row', gap:10, marginHorizontal:16, marginBottom:16 },
  subActBtn:            { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:'#fff', borderRadius:14, paddingVertical:13, borderWidth:1.5, borderColor:C.primary+'44' },
  subActBtnText:        { color:C.primary, fontWeight:'700', fontSize:14 },
  modalHeader:          { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:16, paddingTop:20 },
  modalHeaderTitle:     { color:'#fff', fontSize:17, fontWeight:'700' },
  modalFooter:          { flexDirection:'row', padding:16, gap:10, backgroundColor:'#fff', borderTopWidth:1, borderTopColor:C.border },
  footerBtnPri:         { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:C.primary, paddingVertical:14, borderRadius:14 },
  footerBtnPriTxt:      { color:'#fff', fontWeight:'700', fontSize:15 },
  footerBtnSec:         { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, backgroundColor:C.bg, paddingVertical:14, borderRadius:14, borderWidth:1, borderColor:C.border },
  footerBtnSecTxt:      { color:C.sub, fontWeight:'700', fontSize:14 },
  formLabel:            { fontSize:13, fontWeight:'700', color:C.text, marginBottom:6 },
  formInput:            { backgroundColor:'#fff', borderRadius:12, borderWidth:1.5, borderColor:C.border, padding:14, fontSize:14, color:C.text },
  typeRow:              { flexDirection:'row', flexWrap:'wrap', gap:8 },
  typeChip:             { paddingHorizontal:16, paddingVertical:8, borderRadius:20, backgroundColor:C.bg, borderWidth:1.5, borderColor:C.border },
  typeChipActive:       { backgroundColor:C.primary, borderColor:C.primary },
  typeChipText:         { fontSize:13, color:C.sub, fontWeight:'600' },
  typeChipTextActive:   { color:'#fff' },
  invDetailCard:        { backgroundColor:'#fff', borderRadius:18, padding:20, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:10, elevation:4 },
  invDetailHeader:      { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, paddingBottom:16, borderBottomWidth:1, borderBottomColor:C.border },
  invDetailNum:         { fontSize:18, fontWeight:'800', color:C.primary },
  invDetailClient:      { fontSize:14, color:C.sub, marginTop:4 },
  invDetailAmount:      { fontSize:24, fontWeight:'900', color:C.text },
  invDetailRow:         { flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:1, borderBottomColor:C.border },
  invDetailKey:         { fontSize:13, color:C.sub },
  invDetailVal:         { fontSize:13, color:C.text, fontWeight:'600', flex:1, textAlign:'right' },
  overlayModal:         { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  cancelModal:          { backgroundColor:'#fff', borderTopLeftRadius:24, borderTopRightRadius:24, padding:28, alignItems:'center', gap:14 },
  cancelTitle:          { fontSize:20, fontWeight:'800', color:C.danger },
  cancelBody:           { fontSize:14, color:C.sub, textAlign:'center', lineHeight:22 },
  cancelActions:        { flexDirection:'row', gap:12, width:'100%', marginTop:8 },
});